// Workspace lifecycle, the file tree, secrets (write-only), build kick-off, and
// preview-token minting — all under /v1/workspaces. Authorization is team
// membership on the workspace's team, enforced server-side; the UI just surfaces
// the {code,message} errors.

import { getJSON, sendJSON, request } from "./http.js";
import type { Tag, TagMatch } from "./tags.js";

const BASE = "/v1/workspaces";

export interface Workspace {
  id: string;
  teamId: string;
  name: string;
  /**
   * The workspace's tags, embedded rather than referenced by id: a card renders
   * colored chips without needing the owning team's catalog, which matters on the
   * list page where the rows span several teams. Managed with setWorkspaceTags.
   */
  tags: Tag[];
  created: string;
  updated: string;
  /** The repository this workspace is a branch of. Absent on a workspace
   * without one, and on a server that has no repositories. */
  repository?: string;
  /** The branch the workspace shows; set together with repository. */
  branch?: string;
  /** A pinned branch workspace stays when its branch is gone. */
  pinned?: boolean;
  /** True for the repository's default-branch workspace: deleting it deletes
   * the whole repository. */
  defaultBranch?: boolean;
}

export interface WorkspaceFile {
  path: string;
  size: number;
}

/** WorkspaceFilter narrows the listing; an empty filter returns everything. */
export interface WorkspaceFilter {
  /** Tag ids. An unknown id is not an error — it simply matches nothing. */
  tags?: string[];
  /** "all" (the server default) requires every tag; "any" at least one. */
  tagMatch?: TagMatch;
}

/**
 * normalize fills the fields a workspace row may legitimately omit, so no call
 * site has to null-check them. tags is absent on a response from a server that
 * predates tagging.
 */
function normalize(w: Workspace): Workspace {
  return { ...w, tags: w.tags ?? [] };
}

/**
 * listWorkspaces returns every workspace on the caller's teams, newest first,
 * optionally narrowed to those carrying the given tags.
 */
export async function listWorkspaces(filter: WorkspaceFilter = {}): Promise<Workspace[]> {
  const { items } = await getJSON<{ items: Workspace[] }>(BASE + tagQuery(filter));
  return items.map(normalize);
}

/**
 * tagQuery builds `?tag=a&tag=b&tagMatch=any`. http.ts's query() cannot be used
 * here: it calls URLSearchParams.set, so a repeated key keeps only the last
 * value and a two-tag filter would silently become a one-tag filter.
 */
function tagQuery(f: WorkspaceFilter): string {
  const tags = f.tags ?? [];
  if (tags.length === 0) return "";
  const q = new URLSearchParams();
  for (const id of tags) q.append("tag", id);
  if (f.tagMatch === "any") q.set("tagMatch", "any"); // "all" is the default — omit it
  return `?${q.toString()}`;
}

/** createWorkspace creates a workspace on the given team (member+). */
export async function createWorkspace(teamId: string, name: string): Promise<Workspace> {
  return normalize(await sendJSON<Workspace>(BASE, "POST", { teamId, name }));
}

/** getWorkspace returns one workspace's metadata (viewer+). */
export async function getWorkspace(id: string): Promise<Workspace> {
  return normalize(await getJSON<Workspace>(`${BASE}/${encodeURIComponent(id)}`));
}

/**
 * setWorkspaceTags replaces the workspace's whole tag set (member+). The API has
 * no add/remove verb, so attach and detach both send the new complete array of
 * ids. Every id must belong to the workspace's own team.
 */
export async function setWorkspaceTags(id: string, tags: string[]): Promise<Workspace> {
  return normalize(await sendJSON<Workspace>(`${BASE}/${encodeURIComponent(id)}`, "PATCH", { tags }));
}

/** deleteWorkspace removes the workspace, its files, secrets, and builds (maintainer+). */
export async function deleteWorkspace(id: string): Promise<void> {
  await request(`${BASE}/${encodeURIComponent(id)}`, { method: "DELETE" });
}

// ----- Files -----

// A workspace of a repository answers the header Bino-Commit: the commit its
// file tree shows, or 40 zeros while its branch has no commit yet. A write
// that sends it back as Bino-Base-Commit is refused with 409 commit_conflict
// when someone changed the files since, so an edit never silently overwrites
// a newer one. Without a repository the header is absent, commit stays null
// and no base is sent.

/** commitOf reads the Bino-Commit header; null when the server sent none. */
function commitOf(res: Response): string | null {
  return res.headers.get("Bino-Commit") || null;
}

/** baseHeader sends Bino-Base-Commit only when there is a base to send. */
function baseHeader(base: string | null | undefined): Record<string, string> {
  return base ? { "Bino-Base-Commit": base } : {};
}

/** FileListing is the file tree plus the commit it shows. */
export interface FileListing {
  items: WorkspaceFile[];
  commit: string | null;
}

/** listFiles returns every regular file in the workspace tree, sorted by path. */
export async function listFiles(id: string): Promise<FileListing> {
  const res = await request(`${BASE}/${encodeURIComponent(id)}/files`);
  const { items } = (await res.json()) as { items: WorkspaceFile[] };
  return { items, commit: commitOf(res) };
}

/** readFile returns a file's raw bytes as text (viewer+), plus the commit it was read at. */
export async function readFile(
  id: string,
  filePath: string,
): Promise<{ text: string; commit: string | null }> {
  const res = await request(`${BASE}/${encodeURIComponent(id)}/files/${encodePath(filePath)}`);
  return { text: await res.text(), commit: commitOf(res) };
}

/** readFileBlob returns a file's raw bytes as a Blob (for binary files). */
export async function readFileBlob(id: string, filePath: string): Promise<Blob> {
  const res = await request(`${BASE}/${encodeURIComponent(id)}/files/${encodePath(filePath)}`);
  return res.blob();
}

/**
 * writeFile creates or overwrites a file (member+); parent dirs are created.
 * `base` is the commit the caller last saw; the answer carries the new one.
 */
export async function writeFile(
  id: string,
  filePath: string,
  content: string | Blob,
  base?: string | null,
): Promise<{ file: WorkspaceFile; commit: string | null }> {
  const res = await request(`${BASE}/${encodeURIComponent(id)}/files/${encodePath(filePath)}`, {
    method: "PUT",
    headers: baseHeader(base),
    body: content,
  });
  return { file: (await res.json()) as WorkspaceFile, commit: commitOf(res) };
}

/** deleteFile removes a file or directory subtree (member+). */
export async function deleteFile(
  id: string,
  filePath: string,
  base?: string | null,
): Promise<{ commit: string | null }> {
  const res = await request(`${BASE}/${encodeURIComponent(id)}/files/${encodePath(filePath)}`, {
    method: "DELETE",
    headers: baseHeader(base),
  });
  return { commit: commitOf(res) };
}

/** Change is one file of a commit: its new content (base64), or a delete. */
export interface Change {
  path: string;
  /** The new bytes, base64-encoded. Omitted on a delete. */
  content?: string;
  delete?: boolean;
}

/**
 * commitChanges writes several files as one commit (member+; at most 500
 * changes). Only a workspace of a hosted repository takes it: a workspace
 * outside a repository answers 409 workspace_not_hosted, a mirror workspace
 * 409 workspace_git_locked.
 */
export function commitChanges(
  id: string,
  base: string | null,
  changes: Change[],
  message?: string,
): Promise<{ commit: string }> {
  const body: { base?: string; message?: string; changes: Change[] } = { changes };
  if (base) body.base = base;
  if (message) body.message = message;
  return sendJSON<{ commit: string }>(`${BASE}/${encodeURIComponent(id)}/commits`, "POST", body);
}

// ----- Secrets (write-only; values are never returned) -----

/**
 * SecretScope says where a secret is defined: on the workspace, on its
 * repository, or on its team.
 */
export type SecretScope = "workspace" | "repository" | "team";

export interface SecretName {
  name: string;
  scope: SecretScope;
  /** The name is one the cluster reserves (HOME, PATH, BINO_*, …), so
   * builds ignore the secret. Delete it where it is defined. */
  reserved?: boolean;
}

/**
 * listSecrets returns the NAMES the workspace's builds actually see (viewer+):
 * its own secrets plus the repository and team secrets it inherits, each
 * tagged with its scope, and any inherited secret shadowed by a closer one
 * already dropped. Team-scoped entries are managed on the team page and
 * repository-scoped ones on the repository page, not here.
 */
export async function listSecrets(id: string): Promise<SecretName[]> {
  const { items } = await getJSON<{ items: SecretName[] }>(
    `${BASE}/${encodeURIComponent(id)}/secrets`,
  );
  return items;
}

/** setSecret creates or rotates a WORKSPACE secret (member+). Name must be a valid env var. A name that also exists on the team shadows the inherited value. */
export async function setSecret(id: string, name: string, value: string): Promise<void> {
  await sendJSON(`${BASE}/${encodeURIComponent(id)}/secrets`, "POST", { name, value });
}

/** deleteSecret removes one WORKSPACE secret by name (member+). Inherited team secrets are not in scope here — they answer 404 and are removed on the team page. */
export async function deleteSecret(id: string, name: string): Promise<void> {
  await request(`${BASE}/${encodeURIComponent(id)}/secrets/${encodeURIComponent(name)}`, {
    method: "DELETE",
  });
}

// ----- Build + preview -----

/**
 * startBuild kicks off an async build (member+) and answers 202 with its id.
 * A repository workspace with the data area on queues it ("queued") to run
 * on a read-only checkout of the branch's commit; it answers 429
 * build_queue_full when too many wait, and 409 nothing_committed on a branch
 * without a commit. Any other build starts at once ("pending").
 */
export function startBuild(id: string): Promise<{ buildId: string; status: "queued" | "pending" }> {
  return sendJSON<{ buildId: string; status: "queued" | "pending" }>(
    `${BASE}/${encodeURIComponent(id)}/build`,
    "POST",
    {},
  );
}

export interface PreviewToken {
  token: string;
  expiresAt: string;
}

/**
 * mintPreviewToken issues a short-lived preview token (also set as an HttpOnly
 * cookie scoped to this workspace's preview path). Use previewUrl to build the
 * iframe src.
 */
export function mintPreviewToken(id: string): Promise<PreviewToken> {
  return sendJSON<PreviewToken>(`${BASE}/${encodeURIComponent(id)}/preview/token`, "POST", {});
}

/** previewUrl builds the same-origin preview URL for an iframe (cookie carries auth). */
export function previewUrl(id: string, subPath = ""): string {
  const base = `${BASE}/${encodeURIComponent(id)}/preview`;
  return subPath ? `${base}/${encodePath(subPath)}` : base;
}

/** encodePath encodes each segment of a slash path, preserving the slashes. */
function encodePath(p: string): string {
  return p.split("/").map(encodeURIComponent).join("/");
}
